/**
 * What a comment says about the thing it was written on.
 *
 * Every comment carries a `parentKind`. It exists so the global search can read every
 * discussion in one collection-group query and still be gated per tab: a query of that
 * shape cannot see which parent a comment hung under, so the label stands in for the
 * path. `firestore.rules` writes the same mapping — a comment created under `products`
 * must be labelled `product` and can never be relabelled — which means a comment posted
 * with the wrong label is not a cosmetic mistake, it is a comment the server refuses.
 *
 * That is why this is one table rather than a literal at each call site.
 */
export type CommentParentKind =
  'decision' | 'task' | 'field' | 'product' | 'owner' | 'staff' | 'orgContainer' | 'orgNote'
  | 'orgSharingRule' | 'message'

export const COMMENT_PARENT_KINDS: Record<string, CommentParentKind> = {
  decisions: 'decision',
  tasks: 'task',
  fieldDecisions: 'field',
  products: 'product',
  owners: 'owner',
  staff: 'staff',
  orgContainers: 'orgContainer',
  orgNotes: 'orgNote',
  // A row of the org chart's sharing rules table. Its discussion is gated by
  // `sharingrule.view` — the sub-tab's own grant, not the chart's — exactly as a note's is
  // gated by `orgchart.view`.
  orgSharingRules: 'orgSharingRule',
  // A message thread. The odd one out: every other kind is read by a capability, and a
  // thread is read by being on it. That is why `kindCapability('message')` in
  // `firestore.rules` deliberately matches nothing — the collection-group query behind
  // the global search must not be able to reach a conversation the reader is not part of,
  // so thread comments are readable only down their own path, where the participant list
  // is in reach.
  messages: 'message',
}

/**
 * The kinds whose readers are decided by a capability.
 *
 * Every kind but one. A message thread is read by being on it, and that cannot be
 * expressed as a role — so the global search, the Activity History and everything else
 * that asks "which capability lets somebody see this?" is typed against this narrower set
 * rather than against every kind. A thread that must be left out of one of them is then a
 * compile error rather than a leak nobody noticed.
 */
export type CapabilityGatedKind = Exclude<CommentParentKind, 'message'>

export function parentKindFor(collectionName: string): CommentParentKind {
  const kind = COMMENT_PARENT_KINDS[collectionName]
  // Loudly, and at the point the mistake was made. A comment written without a label, or
  // with the wrong one, is rejected by the rules — better to fail here, where the cause
  // is legible, than at the moment somebody presses Post.
  if (!kind) throw new Error(`No comment label for collection "${collectionName}"`)
  return kind
}
